package ru.chm.gradereport.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.GreenAccent
import ru.chm.gradereport.ui.theme.LocalThemeController
import ru.chm.gradereport.ui.theme.ThemeMode

/** Shared page padding so content does not shift between screens. */
val ScreenContentPadding = 24.dp

private val CompactContentPadding = 12.dp

/** Compact layout below this width (phones in portrait). */
val CompactWidthBreakpoint = 640.dp

@Composable
fun adaptiveContentPadding(maxWidth: Dp): Dp =
    if (maxWidth < CompactWidthBreakpoint) CompactContentPadding else ScreenContentPadding

/** Header toolbar action shown as a button (wide) or menu item (compact). */
data class HeaderAction(
    val label: String,
    val enabled: Boolean = true,
    val onClick: () -> Unit,
)

/**
 * Page header: title/subtitle on the left, theme + actions on the right.
 * On narrow screens actions collapse into an icon overflow menu.
 */
@Composable
fun ScreenHeader(
    title: String,
    modifier: Modifier = Modifier,
    subtitle: String? = null,
    actions: List<HeaderAction> = emptyList(),
) {
    val theme = LocalThemeController.current
    BoxWithConstraints(
        modifier = modifier
            .fillMaxWidth()
            .padding(bottom = 12.dp),
    ) {
        val compact = maxWidth < CompactWidthBreakpoint
        if (compact) {
            CompactScreenHeader(
                title = title,
                subtitle = subtitle,
                actions = actions,
                themeMode = theme?.mode,
                onThemeModeChange = theme?.onModeChange,
            )
        } else {
            WideScreenHeader(
                title = title,
                subtitle = subtitle,
                actions = actions,
                themeMode = theme?.mode,
                onThemeModeChange = theme?.onModeChange,
            )
        }
    }
}

@Composable
private fun WideScreenHeader(
    title: String,
    subtitle: String?,
    actions: List<HeaderAction>,
    themeMode: ThemeMode?,
    onThemeModeChange: ((ThemeMode) -> Unit)?,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
            HeaderTitleBlock(title = title, subtitle = subtitle)
        }
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (themeMode != null && onThemeModeChange != null) {
                ThemeModeDropdown(
                    mode = themeMode,
                    onModeChange = onThemeModeChange,
                )
            }
            actions.forEach { action ->
                SecondaryButton(
                    text = action.label,
                    enabled = action.enabled,
                    onClick = action.onClick,
                )
            }
        }
    }
}

@Composable
private fun CompactScreenHeader(
    title: String,
    subtitle: String?,
    actions: List<HeaderAction>,
    themeMode: ThemeMode?,
    onThemeModeChange: ((ThemeMode) -> Unit)?,
) {
    val hasMenu = themeMode != null || actions.isNotEmpty()
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Column(modifier = Modifier.weight(1f)) {
            HeaderTitleBlock(title = title, subtitle = subtitle)
        }
        if (hasMenu) {
            HeaderOverflowMenu(
                actions = actions,
                themeMode = themeMode,
                onThemeModeChange = onThemeModeChange,
            )
        }
    }
}

@Composable
private fun HeaderTitleBlock(
    title: String,
    subtitle: String?,
) {
    Column {
        SelectableText(
            text = title,
            color = GradeTheme.colors.textPrimary,
            fontSize = 22.sp,
            fontWeight = FontWeight.Bold,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
        )
        Spacer(Modifier.height(2.dp))
        SelectableText(
            text = subtitle?.takeIf { it.isNotBlank() }.orEmpty().ifEmpty { " " },
            color = GradeTheme.colors.textMuted,
            fontSize = 14.sp,
            modifier = Modifier.heightIn(min = 20.dp),
        )
    }
}

@Composable
private fun HeaderOverflowMenu(
    actions: List<HeaderAction>,
    themeMode: ThemeMode?,
    onThemeModeChange: ((ThemeMode) -> Unit)?,
) {
    var expanded by remember { mutableStateOf(false) }
    val colors = GradeTheme.colors

    Box {
        OutlinedButton(
            onClick = { expanded = true },
            shape = RoundedCornerShape(12.dp),
            colors = ButtonDefaults.outlinedButtonColors(contentColor = colors.textPrimary),
            contentPadding = PaddingValues(horizontal = 10.dp, vertical = 10.dp),
        ) {
            OverflowMenuIcon(color = colors.textPrimary)
        }
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            shape = RoundedCornerShape(12.dp),
            containerColor = colors.card,
            border = BorderStroke(1.dp, colors.border),
            modifier = Modifier.widthIn(min = 200.dp),
        ) {
            if (themeMode != null && onThemeModeChange != null) {
                Text(
                    "Тема",
                    color = colors.textMuted,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                )
                ThemeMode.entries.forEach { option ->
                    DropdownMenuItem(
                        text = {
                            Text(
                                option.label,
                                color = if (option == themeMode) GreenAccent else colors.textPrimary,
                                fontWeight = if (option == themeMode) {
                                    FontWeight.SemiBold
                                } else {
                                    FontWeight.Normal
                                },
                            )
                        },
                        onClick = {
                            onThemeModeChange(option)
                            expanded = false
                        },
                    )
                }
                if (actions.isNotEmpty()) {
                    HorizontalDivider(
                        modifier = Modifier.padding(vertical = 4.dp),
                        color = colors.border,
                    )
                }
            }
            actions.forEach { action ->
                DropdownMenuItem(
                    text = {
                        Text(
                            action.label,
                            color = if (action.enabled) {
                                colors.textPrimary
                            } else {
                                colors.textMuted
                            },
                            fontWeight = FontWeight.Medium,
                        )
                    },
                    enabled = action.enabled,
                    onClick = {
                        action.onClick()
                        expanded = false
                    },
                )
            }
        }
    }
}

/** Hamburger icon for the compact overflow button. */
@Composable
private fun OverflowMenuIcon(color: Color) {
    Canvas(modifier = Modifier.size(20.dp)) {
        val stroke = 2.dp.toPx()
        val inset = size.width * 0.14f
        val ys = listOf(size.height * 0.28f, size.height * 0.5f, size.height * 0.72f)
        ys.forEach { y ->
            drawLine(
                color = color,
                start = Offset(inset, y),
                end = Offset(size.width - inset, y),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
        }
    }
}

@Composable
fun ScreenScaffold(
    modifier: Modifier = Modifier,
    scrollable: Boolean = true,
    content: @Composable ColumnScope.() -> Unit,
) {
    BoxWithConstraints(modifier = modifier.fillMaxSize()) {
        val pad = adaptiveContentPadding(maxWidth)
        val base = Modifier
            .fillMaxSize()
            .padding(horizontal = pad, vertical = 12.dp)
        Column(
            modifier = if (scrollable) {
                base.verticalScroll(rememberScrollState())
            } else {
                base
            },
            content = content,
        )
    }
}
